import { supabase } from '../lib/supabase';

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;

export const invoiceService = {
  async getInvoiceLogs({ page = 1, pageSize = 20, status = null, search = '', dateFrom = null, dateTo = null } = {}) {
    let query = supabase
      ?.from('invoice_logs')
      ?.select('*', { count: 'exact' })
      // Store dashboard: show STORE_ORDER rows OR legacy rows where source is NULL (backward compatibility)
      ?.or('source.eq.STORE_ORDER,source.is.null')
      ?.order('created_at', { ascending: false });

    if (status) {
      query = query?.eq('status', status);
    }

    if (search?.trim()) {
      query = query?.or(
        `order_number.ilike.%${search}%,customer_name.ilike.%${search}%,doc_no.ilike.%${search}%,ettn.ilike.%${search}%`
      );
    }

    if (dateFrom) {
      query = query?.gte('created_at', dateFrom);
    }

    if (dateTo) {
      query = query?.lte('created_at', dateTo);
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query?.range(from, to);

    const { data, error, count } = await query;
    if (error) throw error;

    const logs = data || [];

    // For logs missing customer_email, try to fetch from orders → user_profiles
    const missingEmailLogs = logs?.filter(l => !l?.customer_email && l?.order_id);
    if (missingEmailLogs?.length > 0) {
      const orderIds = [...new Set(missingEmailLogs?.map(l => l?.order_id))];
      const { data: orders } = await supabase
        ?.from('orders')
        ?.select('id, guest_email, user_id, user_profiles(email)')
        ?.in('id', orderIds);

      if (orders?.length) {
        const emailMap = {};
        orders?.forEach(o => {
          emailMap[o?.id] = o?.guest_email || o?.user_profiles?.email || null;
        });
        logs?.forEach(l => {
          if (!l?.customer_email && l?.order_id && emailMap?.[l?.order_id]) {
            l.customer_email = emailMap?.[l?.order_id];
          }
        });
      }
    }

    return { data: logs, count: count || 0 };
  },

  async getInvoiceStats() {
    const { data, error } = await supabase
      ?.from('invoice_logs')
      ?.select('status, total_amount')
      // Store dashboard: show STORE_ORDER rows OR legacy rows where source is NULL (backward compatibility)
      ?.or('source.eq.STORE_ORDER,source.is.null');

    if (error) throw error;

    const stats = {
      total: data?.length || 0,
      success: 0,
      error: 0,
      skipped: 0,
      totalAmount: 0,
      successAmount: 0,
    };

    data?.forEach((log) => {
      if (log?.status === 'success') {
        stats.success++;
        stats.successAmount += parseFloat(log?.total_amount || 0);
      } else if (log?.status === 'error') {
        stats.error++;
      } else if (log?.status === 'skipped') {
        stats.skipped++;
      }
      stats.totalAmount += parseFloat(log?.total_amount || 0);
    });

    return stats;
  },

  async getInvoiceById(id) {
    const { data, error } = await supabase?.from('invoice_logs')?.select('*')?.eq('id', id)?.single();

    if (error) throw error;
    return data;
  },

  async getErrorBreakdown() {
    const { data, error } = await supabase?.from('invoice_logs')?.select('error_stage, error_message')?.eq('status', 'error');

    if (error) throw error;

    const breakdown = {};
    data?.forEach((log) => {
      const stage = log?.error_stage || 'unknown';
      breakdown[stage] = (breakdown?.[stage] || 0) + 1;
    });

    return Object.entries(breakdown)?.map(([stage, count]) => ({ stage, count }));
  },

  /**
   * Manually retry a failed invoice log entry.
   * Calls the send-mysoft-invoice edge function with retry context.
   * @param {object} log - The failed invoice_log record
   * @returns {Promise<{success: boolean, invoice?: unknown, error?: string}>}
   */
  async retryInvoice(log) {
    if (!log?.order_id) throw new Error('Bu kayıtta sipariş ID bulunamadı');

    // Increment retry_count on the original log
    const originalId = log?.retry_of || log?.id;
    const currentRetryCount = (log?.retry_count || 0) + 1;

    // Update the original log's retry metadata
    await supabase
      ?.from('invoice_logs')
      ?.update({ retry_count: currentRetryCount, last_retry_at: new Date()?.toISOString() })
      ?.eq('id', originalId);

    // Call the edge function with retry context
    let response = await fetch(`${SUPABASE_URL}/functions/v1/send-mysoft-invoice`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        orderId: log?.order_id,
        isRetry: true,
        retryOfLogId: originalId,
        retryCount: currentRetryCount,
      }),
    });

    let result = await response?.json();
    return result;
  },

  /**
   * Retry a failed invoice by order_id directly.
   * Finds the latest error log for the given orderId and retries it.
   * @param {string} orderId - The order UUID to retry
   * @returns {Promise<{success: boolean, invoice?: unknown, error?: string}>}
   */
  async retryByOrderId(orderId) {
    if (!orderId?.trim()) throw new Error('Order ID gereklidir');

    // Find the latest error log for this order
    const { data: logs, error } = await supabase
      ?.from('invoice_logs')
      ?.select('*')
      ?.eq('order_id', orderId?.trim())
      ?.eq('status', 'error')
      ?.order('created_at', { ascending: false })
      ?.limit(1);

    if (error) throw new Error(`Fatura kaydı bulunamadı: ${error.message}`);
    if (!logs?.length) {
      // No error log found — try direct invocation anyway
      let response = await fetch(`${SUPABASE_URL}/functions/v1/send-mysoft-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ orderId: orderId?.trim(), isRetry: true, retryCount: 1 }),
      });
      return await response?.json();
    }

    return await invoiceService?.retryInvoice(logs?.[0]);
  },

  /**
   * Auto-retry all failed invoice logs that have never been retried.
   * Returns a summary of attempted retries.
   */
  async autoRetryFailedInvoices({ maxRetries = 3 } = {}) {
    // Fetch all error logs that haven't exceeded maxRetries
    const { data: failedLogs, error } = await supabase
      ?.from('invoice_logs')
      ?.select('*')
      ?.eq('status', 'error')
      ?.lt('retry_count', maxRetries)
      ?.order('created_at', { ascending: true });

    if (error) throw error;
    if (!failedLogs?.length) return { attempted: 0, succeeded: 0, failed: 0, results: [] };

    const results = [];
    let succeeded = 0;
    let failed = 0;

    for (const log of failedLogs) {
      try {
        let result = await invoiceService?.retryInvoice(log);
        if (result?.success) {
          succeeded++;
          results?.push({ logId: log?.id, orderNumber: log?.order_number, status: 'success' });
        } else {
          failed++;
          results?.push({ logId: log?.id, orderNumber: log?.order_number, status: 'failed', error: result?.error });
        }
      } catch (err) {
        failed++;
        results?.push({ logId: log?.id, orderNumber: log?.order_number, status: 'failed', error: err?.message });
      }
    }

    return { attempted: failedLogs?.length, succeeded, failed, results };
  },

  /**
   * Send invoice email to customer via send-invoice-email edge function.
   * Fetches signed PDF from Mysoft and sends via Resend.
   * @param {object} log - The invoice_log record (must have status: 'success')
   * @param {string} customerEmail - Customer email address
   * @returns {Promise<{success: boolean, emailId?: string, error?: string}>}
   */
  async sendInvoiceEmail(log, customerEmail) {
    if (!log?.id) throw new Error('Log ID bulunamadı');
    if (!customerEmail?.trim()) throw new Error('Müşteri e-posta adresi gereklidir');

    console.log('[invoiceService.sendInvoiceEmail] Payload:', {
      logId: log?.id,
      ettn: log?.ettn || undefined,
      docNo: log?.doc_no || undefined,
      customerEmail: customerEmail?.trim(),
    });

    let response;
    try {
      response = await fetch(`${SUPABASE_URL}/functions/v1/send-invoice-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          logId: log?.id,
          ettn: log?.ettn || undefined,
          docNo: log?.doc_no || undefined,
          customerEmail: customerEmail?.trim(),
        }),
      });
    } catch (networkErr) {
      // Network error or CORS block — fetch itself threw before we got a response
      console.error('[invoiceService.sendInvoiceEmail] Network/CORS error:', networkErr);
      throw new Error(
        `Ağ hatası veya CORS engeli: ${networkErr?.message || 'Sunucuya ulaşılamadı'}. ` +
        `Edge function URL: ${SUPABASE_URL}/functions/v1/send-invoice-email`
      );
    }

    let result;
    try {
      result = await response?.json();
    } catch (_) {
      throw new Error(`Sunucu yanıtı okunamadı (HTTP ${response.status})`);
    }

    console.log('[invoiceService.sendInvoiceEmail] HTTP status:', response?.status, 'Body:', result);

    if (!response?.ok) {
      const errMsg = result?.error || result?.message || `HTTP ${response?.status} hatası`;
      throw new Error(errMsg);
    }

    return result;
  },
};
